import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LngLatBounds,
  Map as MapLibreMap,
  Marker,
  type GeoJSONSource,
  type LngLatLike,
  type MapMouseEvent,
} from "maplibre-gl";
import type { DriverLocation, PublicSnapshot } from "@paddock-signal/contracts";

import { loadCircuitAsset, type RenderCircuitAsset } from "../circuit-assets";
import { recordClientDiagnostic } from "../diagnostics";
import {
  activeEstimatedLocations,
  circuitMarkerOpacity,
  clusterBounds,
  clusterDriverLocations,
  distanceKilometers,
} from "../globe-model";
import { createElevatedCircuitLayer } from "./ElevatedCircuitLayer";

type Driver = PublicSnapshot["drivers"][number];
type Circuit = PublicSnapshot["circuits"][number];

interface SignalGlobeProps {
  circuits: Circuit[];
  drivers: Driver[];
  locations: DriverLocation[];
  mapDetails: MapDetailSettings;
  onSelectDriver: (driverId: string) => void;
  onSelectGroup: (driverIds: string[]) => void;
  onSelectCircuit: (circuitId: string) => void;
  selectedDriverId: string | null;
  visibleDriverIds: ReadonlySet<string>;
}

export interface MapDetailSettings {
  countryLabels: boolean;
  circuits: boolean;
}

type CircuitRenderer = "idle" | "loading" | "3d" | "2d" | "unavailable";

const atlasStyleUrl = "https://tiles.openfreemap.org/styles/liberty";
const globalCenter: [number, number] = [8, 28];
const desktopGlobalZoom = 1.42;
const mobileGlobalZoom = 1.04;
const desktopGlobalMinZoom = 1.34;
const mobileGlobalMinZoom = 0.92;
const clusterExpansionZoom = 11.5;
const circuitMarkerHideZoom = 11.5;
const mobileCircuitZoomOut = 2;

function globalZoomForViewport(): number {
  return window.matchMedia("(max-width: 759px)").matches
    ? mobileGlobalZoom
    : desktopGlobalZoom;
}

function globalMinZoomForViewport(): number {
  return window.matchMedia("(max-width: 759px)").matches
    ? mobileGlobalMinZoom
    : desktopGlobalMinZoom;
}
const hiddenAtlasLayers = [
  "aeroway",
  "building",
  "poi_",
  "transit_rail",
  "major_rail",
];

function circuitAssetCenter(
  asset: RenderCircuitAsset,
  fallback: [number, number],
): [number, number] {
  const [
    [minimumLongitude, minimumLatitude],
    [maximumLongitude, maximumLatitude],
  ] = asset.bounds;
  if (
    [
      minimumLongitude,
      minimumLatitude,
      maximumLongitude,
      maximumLatitude,
    ].every(Number.isFinite)
  ) {
    return [
      (minimumLongitude + maximumLongitude) / 2,
      (minimumLatitude + maximumLatitude) / 2,
    ];
  }
  return fallback;
}

function isRoadLayer(layerId: string): boolean {
  return (
    layerId.startsWith("road_") ||
    layerId.startsWith("tunnel_") ||
    layerId.startsWith("bridge_") ||
    layerId.startsWith("highway-")
  );
}

function applyMapDetails(map: MapLibreMap, settings: MapDetailSettings): void {
  for (const layer of map.getStyle().layers) {
    if (isRoadLayer(layer.id)) {
      map.setLayoutProperty(layer.id, "visibility", "visible");
    }
    if (layer.id.startsWith("label_country_")) {
      map.setLayoutProperty(
        layer.id,
        "visibility",
        settings.countryLabels ? "visible" : "none",
      );
    }
  }
}

function tuneAtlasStyle(map: MapLibreMap): void {
  map.setProjection({ type: "globe" });
  for (const layer of map.getStyle().layers) {
    if (hiddenAtlasLayers.some((token) => layer.id.includes(token))) {
      map.setLayoutProperty(layer.id, "visibility", "none");
      continue;
    }
    if (layer.type === "background") {
      map.setPaintProperty(layer.id, "background-color", "#E8F2FF");
    }
    if (layer.id === "water") {
      map.setPaintProperty(layer.id, "fill-color", "#E8F2FF");
    }
    if (layer.id === "natural_earth") {
      map.setPaintProperty(layer.id, "raster-saturation", 0.18);
      map.setPaintProperty(layer.id, "raster-contrast", 0.06);
      map.setPaintProperty(layer.id, "raster-hue-rotate", 110);
      map.setPaintProperty(layer.id, "raster-brightness-min", 0.5);
      map.setPaintProperty(layer.id, "raster-brightness-max", 0.96);
    }
    if (layer.id.startsWith("waterway_")) {
      map.setPaintProperty(layer.id, "line-color", "#91B5B2");
    }
    if (layer.id.startsWith("landcover_")) {
      map.setPaintProperty(layer.id, "fill-opacity", 0.58);
    }
    if (layer.id === "park" || layer.id.startsWith("landcover_grass")) {
      map.setPaintProperty(layer.id, "fill-color", "#CAD4B7");
    }
    if (layer.id.startsWith("landcover_wood")) {
      map.setPaintProperty(layer.id, "fill-color", "#B7C5A7");
    }
    if (layer.id.includes("_casing") && layer.type === "line") {
      map.setPaintProperty(layer.id, "line-color", "#9B8E78");
      map.setPaintProperty(layer.id, "line-opacity", 0.52);
    }
    if (layer.id.startsWith("road_") && layer.type === "line") {
      map.setPaintProperty(layer.id, "line-color", "#FFF9EA");
    }
    if (layer.id.startsWith("boundary_") && layer.type === "line") {
      map.setPaintProperty(layer.id, "line-color", "#4C4D49");
      map.setPaintProperty(layer.id, "line-opacity", 0.54);
    }
    if (layer.type === "symbol") {
      if ("text-color" in (layer.paint ?? {})) {
        map.setPaintProperty(layer.id, "text-color", "#111111");
      }
      if ("text-halo-color" in (layer.paint ?? {})) {
        map.setPaintProperty(layer.id, "text-halo-color", "#EAF0E9");
      }
    }
  }
}

function markerButton(driver: Driver, band: string): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `driver-marker driver-marker--${band}`;
  button.dataset.driverId = driver.id;
  button.setAttribute(
    "aria-label",
    `Open ${driver.givenName} ${driver.familyName}`,
  );
  button.style.setProperty("--driver-accent", driver.accentColor);
  const plate = document.createElement("span");
  plate.className = "driver-marker__plate";
  const acronym = document.createElement("span");
  acronym.className = "driver-marker__acronym";
  acronym.textContent = driver.acronym;
  const number = document.createElement("span");
  number.className = "driver-marker__number";
  number.textContent = `#${driver.permanentNumber}`;
  plate.append(acronym, number);
  button.append(plate);
  return button;
}

function groupMarker(drivers: Driver[]): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "driver-cluster";
  button.dataset.driverIds = drivers.map((driver) => driver.id).join(",");
  button.setAttribute(
    "aria-label",
    `Open ${String(drivers.length)} nearby drivers`,
  );
  const plate = document.createElement("span");
  plate.className = "driver-cluster__plate";
  for (const driver of drivers) {
    const badge = document.createElement("span");
    badge.className = "driver-cluster__badge";
    badge.style.setProperty("--driver-accent", driver.accentColor);
    const acronym = document.createElement("strong");
    acronym.textContent = driver.acronym;
    const number = document.createElement("small");
    number.textContent = `#${driver.permanentNumber}`;
    badge.append(acronym, number);
    plate.append(badge);
  }
  const count = document.createElement("span");
  count.className = "driver-cluster__count";
  count.textContent = `+${String(drivers.length)}`;
  plate.append(count);
  button.append(plate);
  return button;
}

function circuitMarker(circuit: Circuit): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `circuit-marker circuit-marker--${circuit.status} circuit-marker--${circuit.assetState}`;
  button.dataset.circuitId = circuit.id;
  button.setAttribute(
    "aria-label",
    circuit.assetState === "unavailable"
      ? `${circuit.name} circuit asset unavailable`
      : `Open ${circuit.assetState === "3d" ? "3D " : ""}${circuit.name}`,
  );
  const label = document.createElement("span");
  label.className = "circuit-marker__label";
  label.textContent = circuit.name;
  button.append(label);
  return button;
}

function reducedRendering(): boolean {
  const memory = (navigator as Navigator & { deviceMemory?: number })
    .deviceMemory;
  return (
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
    navigator.hardwareConcurrency <= 2 ||
    (memory !== undefined && memory <= 2)
  );
}

function removeCircuitLayers(map: MapLibreMap, circuitId: string): void {
  for (const layerId of [
    `circuit-elevated-${circuitId}`,
    `circuit-ribbon-${circuitId}`,
    `circuit-ribbon-edge-${circuitId}`,
    `circuit-hit-${circuitId}`,
    `circuit-line-${circuitId}`,
    `circuit-glow-${circuitId}`,
  ]) {
    if (map.getLayer(layerId)) map.removeLayer(layerId);
  }
  for (const sourceId of [
    `circuit-source-${circuitId}`,
    `circuit-ribbon-source-${circuitId}`,
  ]) {
    if (map.getSource(sourceId)) map.removeSource(sourceId);
  }
}

function circuitLayerIds(map: MapLibreMap): string[] {
  return map
    .getStyle()
    .layers.filter(
      (layer) =>
        layer.id.startsWith("circuit-ribbon-") ||
        layer.id.startsWith("circuit-hit-") ||
        layer.id.startsWith("circuit-line-"),
    )
    .map((layer) => layer.id);
}

function circuitIdFromLayerId(layerId: string): string | null {
  const prefix = layerId.startsWith("circuit-ribbon-")
    ? "circuit-ribbon-"
    : layerId.startsWith("circuit-hit-")
      ? "circuit-hit-"
      : layerId.startsWith("circuit-line-")
        ? "circuit-line-"
        : null;
  return prefix === null ? null : layerId.slice(prefix.length);
}

function isGeoJSONSource(source: unknown): source is GeoJSONSource {
  return (
    typeof source === "object" &&
    source !== null &&
    "setData" in source &&
    typeof source.setData === "function"
  );
}

function addCircuitLine(
  map: MapLibreMap,
  asset: RenderCircuitAsset,
  forceTwoDimensions = false,
): void {
  const sourceId = `circuit-source-${asset.circuitId}`;
  const data = {
    type: "Feature" as const,
    properties: {},
    geometry: {
      type: "LineString" as const,
      coordinates: asset.centerline.map(([longitude, latitude]) => [
        longitude,
        latitude,
      ]),
    },
  };
  const existingSource = map.getSource(sourceId);
  if (existingSource) {
    if (!isGeoJSONSource(existingSource)) {
      throw new TypeError(`Circuit source ${sourceId} is not GeoJSON.`);
    }
    void existingSource.setData(data);
    return;
  }
  map.addSource(sourceId, { type: "geojson", data });
  /** Keep the visual line slim while giving pointer users a generous,
   * forgiving corridor around the complete route. The 1% opacity keeps the
   * layer queryable by MapLibre without adding a visible second stroke. */
  map.addLayer({
    id: `circuit-hit-${asset.circuitId}`,
    type: "line",
    source: sourceId,
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#000000",
      "line-width": 72,
      "line-opacity": 0.01,
    },
  });
  const isThreeDimensional = asset.mode === "3d" && !forceTwoDimensions;
  map.addLayer({
    id: `circuit-glow-${asset.circuitId}`,
    type: "line",
    source: sourceId,
    paint: {
      "line-color": isThreeDimensional ? "#FFD4A1" : "#FFF1D4",
      "line-width": isThreeDimensional ? 8 : 14,
      "line-blur": isThreeDimensional ? 3 : 6,
      "line-opacity": isThreeDimensional ? 0 : 0.96,
    },
  });
  map.addLayer({
    id: `circuit-line-${asset.circuitId}`,
    type: "line",
    source: sourceId,
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": isThreeDimensional ? "#112E3D" : "#D85B3E",
      "line-width": isThreeDimensional ? 2 : 4,
      "line-opacity": isThreeDimensional ? 0 : 0.98,
    },
  });
}

interface RibbonPoint {
  center: [number, number];
  left: [number, number];
  right: [number, number];
  leftHeight: number;
  rightHeight: number;
}

function buildNativeRibbonPoints(asset: RenderCircuitAsset): RibbonPoint[] {
  const minimum = Math.min(...asset.centerline.map((point) => point[2]));
  const elevationScale = 16;
  const surfaceThickness = 8;
  const projected = asset.centerline.map(
    ([longitude, latitude, elevation]) => ({
      longitude,
      latitude,
      elevation,
    }),
  );
  return projected.map((point, index) => {
    const previous = projected[Math.max(0, index - 1)] ?? point;
    const next = projected[Math.min(projected.length - 1, index + 1)] ?? point;
    const latitudeMeters = 111_320;
    const longitudeMeters =
      latitudeMeters * Math.cos((point.latitude * Math.PI) / 180);
    const directionX = (next.longitude - previous.longitude) * longitudeMeters;
    const directionY = (next.latitude - previous.latitude) * latitudeMeters;
    const magnitude = Math.max(Math.hypot(directionX, directionY), 1e-6);
    const halfWidth = 16;
    const offsetX = (-directionY / magnitude) * halfWidth;
    const offsetY = (directionX / magnitude) * halfWidth;
    const bankAngle = asset.bankAngleDegrees?.[index] ?? 0;
    const bankRise =
      Math.sin((bankAngle * Math.PI) / 180) * 22 * elevationScale;
    /** Fill-extrusion heights are measured from the map surface. Keeping the
     * base at zero (below) makes the exaggerated relief a connected wall
     * instead of a ribbon that appears to hover above the atlas. */
    const height =
      surfaceThickness + (point.elevation - minimum) * elevationScale;
    const toCoordinate = (east: number, north: number): [number, number] => [
      point.longitude + east / longitudeMeters,
      point.latitude + north / latitudeMeters,
    ];
    return {
      center: [point.longitude, point.latitude],
      left: toCoordinate(offsetX, offsetY),
      right: toCoordinate(-offsetX, -offsetY),
      leftHeight: height + bankRise,
      rightHeight: height - bankRise,
    };
  });
}

function interpolateRibbonPoint(
  from: RibbonPoint,
  to: RibbonPoint,
  amount: number,
): RibbonPoint {
  const interpolateCoordinate = (
    fromCoordinate: [number, number],
    toCoordinate: [number, number],
  ): [number, number] => [
    fromCoordinate[0] + (toCoordinate[0] - fromCoordinate[0]) * amount,
    fromCoordinate[1] + (toCoordinate[1] - fromCoordinate[1]) * amount,
  ];
  return {
    center: interpolateCoordinate(from.center, to.center),
    left: interpolateCoordinate(from.left, to.left),
    right: interpolateCoordinate(from.right, to.right),
    leftHeight: from.leftHeight + (to.leftHeight - from.leftHeight) * amount,
    rightHeight:
      from.rightHeight + (to.rightHeight - from.rightHeight) * amount,
  };
}

function smoothRibbonPoints(points: RibbonPoint[]): RibbonPoint[] {
  if (points.length < 4) return points;
  const first = points[0];
  const last = points[points.length - 1];
  const closed =
    Math.hypot(
      first.center[0] - last.center[0],
      first.center[1] - last.center[1],
    ) < 0.00005;
  const source = closed ? points.slice(0, -1) : points;
  if (source.length < 3) return points;
  const smoothed: RibbonPoint[] = [];
  const sourceFirst = source[0];
  const sourceLast = source[source.length - 1];
  if (!closed) smoothed.push(sourceFirst);
  const segmentCount = closed ? source.length : source.length - 1;
  for (let index = 0; index < segmentCount; index += 1) {
    const from = source[index];
    const to = source[(index + 1) % source.length];
    smoothed.push(
      interpolateRibbonPoint(from, to, 0.25),
      interpolateRibbonPoint(from, to, 0.75),
    );
  }
  if (!closed) smoothed.push(sourceLast);
  return closed ? [...smoothed, smoothed[0]] : smoothed;
}

function addNativeRibbonSurface(
  map: MapLibreMap,
  asset: RenderCircuitAsset,
): void {
  if (asset.mode !== "3d") return;
  const sourceId = `circuit-ribbon-source-${asset.circuitId}`;
  const points = smoothRibbonPoints(
    smoothRibbonPoints(buildNativeRibbonPoints(asset)),
  );
  const centerline = points.map((point) => point.center);
  const leftEdge = points.map((point) => point.left);
  const rightEdge = points.map((point) => point.right);
  const average = (values: number[]) =>
    values.reduce((total, value) => total + value, 0) /
    Math.max(values.length, 1);
  const leftHeight = average(points.map((point) => point.leftHeight));
  const rightHeight = average(points.map((point) => point.rightHeight));
  const polygon = (
    edge: [number, number][],
    inner: [number, number][],
  ): [number, number][][] => [[...edge, ...inner.slice().reverse(), edge[0]]];
  const features = [
    {
      type: "Feature" as const,
      properties: {
        base: 0,
        color: "#0F5B66",
        height: leftHeight,
      },
      geometry: {
        type: "Polygon" as const,
        coordinates: polygon(leftEdge, centerline),
      },
    },
    {
      type: "Feature" as const,
      properties: {
        base: 0,
        color: "#F08A3C",
        height: rightHeight,
      },
      geometry: {
        type: "Polygon" as const,
        coordinates: polygon(centerline, rightEdge),
      },
    },
  ];
  const data = {
    type: "FeatureCollection" as const,
    features,
  };
  const existingSource = map.getSource(sourceId);
  if (existingSource) {
    if (!isGeoJSONSource(existingSource)) {
      throw new TypeError(`Circuit ribbon source ${sourceId} is not GeoJSON.`);
    }
    void existingSource.setData(data);
    return;
  }
  map.addSource(sourceId, { type: "geojson", data });
  map.addLayer({
    id: `circuit-ribbon-${asset.circuitId}`,
    type: "fill-extrusion",
    source: sourceId,
    paint: {
      "fill-extrusion-color": ["get", "color"],
      "fill-extrusion-height": ["get", "height"],
      "fill-extrusion-base": ["get", "base"],
      "fill-extrusion-opacity": 1,
      "fill-extrusion-vertical-gradient": false,
    },
  });
}

function keepCircuitLabelsVisible(markers: readonly Marker[]): void {
  /** Circuit labels are the globe's primary way to discover a track. Do not
   * hide them based on projected overlap: that made names appear to load only
   * after zooming in. Overlap is preferable to an undiscoverable circuit, and
   * the drawer remains the detailed view once a label is selected. */
  for (const marker of markers) {
    marker.getElement().dataset.collisionHidden = "false";
  }
}

function updateCircuitVisibility(
  map: MapLibreMap,
  markers: readonly Marker[],
): void {
  const hidden = map.getZoom() >= circuitMarkerHideZoom;
  for (const marker of markers) {
    const element = marker.getElement();
    element.dataset.zoomHidden = String(hidden);
    element.setAttribute("aria-hidden", String(hidden));
    element.tabIndex = hidden ? -1 : 0;
  }
}

function projectionErrors(
  map: MapLibreMap,
  markers: readonly Marker[],
): { id: string; error: number }[] {
  const container = map.getContainer().getBoundingClientRect();
  return markers.flatMap((marker) => {
    const element = marker.getElement();
    if (Number.parseFloat(getComputedStyle(element).opacity) < 0.8) {
      return [];
    }
    const transform = /translate\((-?[\d.]+)px,\s*(-?[\d.]+)px\)/.exec(
      element.style.transform,
    );
    if (transform === null) return [];
    const expected = { x: Number(transform[1]), y: Number(transform[2]) };
    const rect = element.getBoundingClientRect();
    const actualX = rect.left - container.left + rect.width / 2;
    const actualY = rect.top - container.top + rect.height / 2;
    return [
      {
        id:
          element.dataset.driverId ??
          element.dataset.driverIds ??
          element.dataset.circuitId ??
          "marker",
        error: Math.hypot(actualX - expected.x, actualY - expected.y),
      },
    ];
  });
}

function updateMarkerDepth(map: MapLibreMap, markers: readonly Marker[]): void {
  const center = map.getCenter();
  const centerCoordinates: [number, number] = [center.lng, center.lat];
  for (const marker of markers) {
    const coordinates = marker.getLngLat();
    const isCircuit = marker.getElement().dataset.circuitId !== undefined;
    const distanceFromView = distanceKilometers(centerCoordinates, [
      coordinates.lng,
      coordinates.lat,
    ]);
    const farSide = map.getZoom() < 5 && distanceFromView > 9_850;
    /** Keep circuit names legible on the far hemisphere at the global view;
     * driver badges can remain subdued because their detail is secondary. */
    marker.getElement().style.opacity = isCircuit
      ? circuitMarkerOpacity(distanceFromView, map.getZoom()).toFixed(3)
      : farSide
        ? "0.18"
        : "1";
    marker.getElement().dataset.hemisphere = farSide ? "far" : "near";
  }
}

function staticClusters(
  drivers: Driver[],
  locations: DriverLocation[],
  visibleDriverIds: ReadonlySet<string>,
): { cluster: Driver[]; individuals: Driver[] } {
  const estimated = locations.filter(
    (location): location is Extract<DriverLocation, { status: "estimated" }> =>
      location.status === "estimated" &&
      visibleDriverIds.has(location.driverId),
  );
  const clustered = new Set<string>();
  for (const location of estimated) {
    if (
      estimated.some(
        (candidate) =>
          candidate.driverId !== location.driverId &&
          distanceKilometers(candidate.coordinates, location.coordinates) < 35,
      )
    ) {
      clustered.add(location.driverId);
    }
  }
  return {
    cluster: drivers.filter((driver) => clustered.has(driver.id)),
    individuals: drivers.filter(
      (driver) =>
        visibleDriverIds.has(driver.id) &&
        !clustered.has(driver.id) &&
        estimated.some((location) => location.driverId === driver.id),
    ),
  };
}

export function SignalGlobe({
  circuits,
  drivers,
  locations,
  mapDetails,
  onSelectDriver,
  onSelectGroup,
  onSelectCircuit,
  selectedDriverId,
  visibleDriverIds,
}: SignalGlobeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const driverMarkersRef = useRef<Marker[]>([]);
  const circuitMarkersRef = useRef<Marker[]>([]);
  const loadedAssetsRef = useRef(new Map<string, RenderCircuitAsset>());
  const circuitsRef = useRef(circuits);
  circuitsRef.current = circuits;
  const focusedCircuitRef = useRef<string | null>(null);
  const renderedCircuitRef = useRef<string | null>(null);
  const circuitLoadingRef = useRef(false);
  const staticMode =
    new URLSearchParams(window.location.search).get("map") === "static";
  const [failed, setFailed] = useState(staticMode);
  const [ready, setReady] = useState(staticMode);
  const [clusterCount, setClusterCount] = useState(0);
  const [markerCount, setMarkerCount] = useState(0);
  const [circuitRenderer, setCircuitRenderer] =
    useState<CircuitRenderer>("idle");
  const [mapHighlight, setMapHighlight] = useState(false);
  const [loadedCircuitId, setLoadedCircuitId] = useState<string | null>(null);
  const globeRef = useRef<HTMLDivElement>(null);

  const renderCircuit = useCallback(
    async (circuit: Circuit, focus: boolean) => {
      const map = mapRef.current;
      if (map === null) {
        setCircuitRenderer("unavailable");
        return;
      }
      const reduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      const mobileViewport = window.matchMedia("(max-width: 759px)").matches;
      const focusZoom = mobileViewport
        ? Math.max(map.getMinZoom(), map.getMaxZoom() - mobileCircuitZoomOut)
        : map.getMaxZoom();
      if (focus) {
        focusedCircuitRef.current = circuit.id;
        map.flyTo({
          center: circuit.coordinates,
          zoom: focusZoom,
          pitch: reduced ? 0 : mobileViewport ? 42 : 55,
          offset: [0, 0],
          duration: reduced ? 0 : 1_250,
          essential: false,
        });
      }
      if (circuit.assetState === "unavailable") {
        if (renderedCircuitRef.current !== null) {
          removeCircuitLayers(map, renderedCircuitRef.current);
          renderedCircuitRef.current = null;
        }
        setMapHighlight(false);
        setLoadedCircuitId(circuit.id);
        setCircuitRenderer("unavailable");
        return;
      }
      setCircuitRenderer("loading");
      circuitLoadingRef.current = true;
      let loadedAsset: RenderCircuitAsset | undefined;
      try {
        const asset =
          loadedAssetsRef.current.get(circuit.id) ??
          (await loadCircuitAsset(circuit));
        loadedAsset = asset;
        loadedAssetsRef.current.set(circuit.id, asset);
        if (focus && focusedCircuitRef.current === circuit.id) {
          map.flyTo({
            center: circuitAssetCenter(asset, circuit.coordinates),
            zoom: focusZoom,
            pitch: reduced ? 0 : mobileViewport ? 42 : 55,
            offset: [0, 0],
            duration: reduced ? 0 : 700,
            essential: false,
          });
        }
        const previous = renderedCircuitRef.current;
        if (previous) removeCircuitLayers(map, previous);
        renderedCircuitRef.current = circuit.id;
        setLoadedCircuitId(circuit.id);
        const useTwoDimensions = reducedRendering() || asset.mode !== "3d";
        if (useTwoDimensions) {
          addCircuitLine(map, asset, true);
          setMapHighlight(true);
          setCircuitRenderer("2d");
        } else {
          const fallback = () => {
            queueMicrotask(() => {
              if (renderedCircuitRef.current !== circuit.id) return;
              removeCircuitLayers(map, circuit.id);
              addCircuitLine(map, asset, true);
              setMapHighlight(true);
              setCircuitRenderer("2d");
              recordClientDiagnostic("circuit-webgl-fallback");
            });
          };
          addCircuitLine(map, asset);
          addNativeRibbonSurface(map, asset);
          map.addLayer(
            createElevatedCircuitLayer(asset, fallback, () => {
              setCircuitRenderer("3d");
            }),
          );
          setMapHighlight(true);
        }
      } catch {
        if (loadedAsset !== undefined) {
          addCircuitLine(map, loadedAsset, true);
          setMapHighlight(true);
          if (focus && focusedCircuitRef.current === circuit.id) {
            map.flyTo({
              center: circuitAssetCenter(loadedAsset, circuit.coordinates),
              zoom: focusZoom,
              pitch: reduced ? 0 : mobileViewport ? 42 : 55,
              offset: [0, 0],
              duration: reduced ? 0 : 700,
              essential: false,
            });
          }
        }
        setLoadedCircuitId(circuit.id);
        setCircuitRenderer("unavailable");
      } finally {
        circuitLoadingRef.current = false;
      }
    },
    [],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (container === null || staticMode) return;
    const map = new MapLibreMap({
      container,
      style: atlasStyleUrl,
      center: globalCenter,
      zoom: globalZoomForViewport(),
      minZoom: globalMinZoomForViewport(),
      maxZoom: 15,
      attributionControl: false,
      canvasContextAttributes: { antialias: true },
      maxPitch: 60,
      pitchWithRotate: false,
      renderWorldCopies: false,
    });
    mapRef.current = map;
    map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();
    const signalMotion = (moving: boolean) =>
      window.dispatchEvent(
        new CustomEvent("ps:map-motion", { detail: { moving } }),
      );
    map.on("movestart", () => signalMotion(true));
    map.on("moveend", () => signalMotion(false));
    let styleReady = false;
    map.on("error", () => {
      if (!styleReady) setFailed(true);
    });
    const resetMap = () => {
      map.flyTo({
        center: globalCenter,
        zoom: globalZoomForViewport(),
        pitch: 0,
        duration: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? 0
          : 850,
        essential: false,
      });
    };
    window.addEventListener("ps:reset-map", resetMap);
    map.on("style.load", () => {
      styleReady = true;
      tuneAtlasStyle(map);
    });
    const selectCircuitFromMap = (event: MapMouseEvent) => {
      const layers = circuitLayerIds(map);
      if (layers.length === 0) return;
      const features = map.queryRenderedFeatures(event.point, { layers });
      if (features.length === 0) return;
      const hit = features[0].layer.id;
      const circuitId = circuitIdFromLayerId(hit);
      if (circuitId !== null) {
        event.originalEvent.stopPropagation();
        onSelectCircuit(circuitId);
        const circuit = circuitsRef.current.find(
          (candidate) => candidate.id === circuitId,
        );
        if (circuit !== undefined) void renderCircuit(circuit, true);
      }
    };
    map.on("click", selectCircuitFromMap);
    map.on("load", () => {
      setFailed(false);
      setReady(true);
    });
    const canvas = map.getCanvas();
    const contextLost = (event: Event) => {
      event.preventDefault();
      setCircuitRenderer("2d");
    };
    const contextRestored = () => {
      const circuitId = renderedCircuitRef.current;
      const asset = circuitId
        ? loadedAssetsRef.current.get(circuitId)
        : undefined;
      if (asset) {
        queueMicrotask(() => {
          if (!map.getSource(`circuit-source-${asset.circuitId}`)) {
            addCircuitLine(map, asset, true);
          }
          setCircuitRenderer("2d");
        });
      }
    };
    canvas.addEventListener("webglcontextlost", contextLost);
    canvas.addEventListener("webglcontextrestored", contextRestored);

    return () => {
      driverMarkersRef.current.forEach((marker) => marker.remove());
      circuitMarkersRef.current.forEach((marker) => marker.remove());
      canvas.removeEventListener("webglcontextlost", contextLost);
      canvas.removeEventListener("webglcontextrestored", contextRestored);
      window.removeEventListener("ps:reset-map", resetMap);
      map.off("click", selectCircuitFromMap);
      map.remove();
      mapRef.current = null;
    };
  }, [onSelectCircuit, renderCircuit, staticMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || map === null || staticMode) return;
    applyMapDetails(map, mapDetails);
  }, [mapDetails, ready, staticMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || map === null || staticMode) return;
    const driverById = new Map(drivers.map((driver) => [driver.id, driver]));
    const snapshot = { drivers, locations };
    const rebuild = () => {
      driverMarkersRef.current.forEach((marker) => marker.remove());
      driverMarkersRef.current = [];
      const estimated = activeEstimatedLocations(snapshot, visibleDriverIds);
      const clusters = clusterDriverLocations(
        estimated,
        (coordinates) => map.project(coordinates),
        map.getZoom() >= 8 ? 68 : 92,
      );
      setClusterCount(
        clusters.filter((cluster) => cluster.driverIds.length > 1).length,
      );
      setMarkerCount(estimated.length);
      for (const cluster of clusters) {
        if (cluster.driverIds.length > 1) {
          const clusterDrivers = cluster.driverIds.flatMap((driverId) => {
            const driver = driverById.get(driverId);
            return driver ? [driver] : [];
          });
          const element = groupMarker(clusterDrivers);
          element.addEventListener("click", () => {
            if (map.getZoom() >= clusterExpansionZoom) {
              onSelectGroup(cluster.driverIds);
              return;
            }
            const bounds = clusterBounds(cluster);
            const samePoint =
              bounds[0][0] === bounds[1][0] && bounds[0][1] === bounds[1][1];
            if (samePoint) {
              map.flyTo({
                center: cluster.coordinates,
                zoom: clusterExpansionZoom,
                duration: 850,
                essential: false,
              });
            } else {
              map.fitBounds(new LngLatBounds(bounds[0], bounds[1]), {
                padding: 120,
                maxZoom: clusterExpansionZoom,
                duration: 850,
                essential: false,
              });
            }
          });
          driverMarkersRef.current.push(
            new Marker({
              element,
              anchor: "center",
              opacityWhenCovered: 0.2,
              subpixelPositioning: true,
            })
              .setLngLat(cluster.coordinates)
              .addTo(map),
          );
          continue;
        }
        const location = cluster.locations[0];
        const driver = driverById.get(location.driverId);
        if (!driver) continue;
        const element = markerButton(driver, location.confidenceBand);
        element.addEventListener("click", () => {
          onSelectDriver(driver.id);
        });
        driverMarkersRef.current.push(
          new Marker({
            element,
            anchor: "center",
            opacityWhenCovered: 0.2,
            subpixelPositioning: true,
          })
            .setLngLat(location.coordinates)
            .addTo(map),
        );
      }
    };
    rebuild();
    map.on("moveend", rebuild);
    return () => {
      map.off("moveend", rebuild);
      driverMarkersRef.current.forEach((marker) => marker.remove());
      driverMarkersRef.current = [];
    };
  }, [
    drivers,
    locations,
    onSelectDriver,
    onSelectGroup,
    ready,
    staticMode,
    visibleDriverIds,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || map === null || staticMode) return;
    if (!mapDetails.circuits) {
      circuitMarkersRef.current.forEach((marker) => marker.remove());
      circuitMarkersRef.current = [];
      if (renderedCircuitRef.current !== null) {
        removeCircuitLayers(map, renderedCircuitRef.current);
        renderedCircuitRef.current = null;
      }
      setMapHighlight(false);
      setLoadedCircuitId(null);
      setCircuitRenderer("idle");
      return;
    }
    circuitMarkersRef.current.forEach((marker) => marker.remove());
    circuitMarkersRef.current = circuits.map((circuit) => {
      const element = circuitMarker(circuit);
      element.addEventListener("click", () => {
        onSelectCircuit(circuit.id);
        void renderCircuit(circuit, true);
      });
      return new Marker({
        element,
        anchor: "center",
        opacityWhenCovered: 0.16,
        subpixelPositioning: true,
      })
        .setLngLat(circuit.coordinates)
        .addTo(map);
    });
    const updateCircuitLayout = () => {
      keepCircuitLabelsVisible(circuitMarkersRef.current);
      updateCircuitVisibility(map, circuitMarkersRef.current);
      globeRef.current?.setAttribute(
        "data-circuit-zoom",
        map.getZoom().toFixed(2),
      );
    };
    updateCircuitLayout();
    const loadNearby = () => {
      if (map.getZoom() < 8 || circuitLoadingRef.current) return;
      const center = map.getCenter();
      const nearby = circuits.find(
        (circuit) =>
          circuit.assetState !== "unavailable" &&
          distanceKilometers([center.lng, center.lat], circuit.coordinates) <=
            100,
      );
      if (nearby && renderedCircuitRef.current !== nearby.id) {
        void renderCircuit(nearby, false);
      }
    };
    loadNearby();
    map.on("moveend", loadNearby);
    map.on("moveend", updateCircuitLayout);
    return () => {
      map.off("moveend", loadNearby);
      map.off("moveend", updateCircuitLayout);
      circuitMarkersRef.current.forEach((marker) => marker.remove());
      circuitMarkersRef.current = [];
    };
  }, [
    circuits,
    mapDetails.circuits,
    onSelectCircuit,
    ready,
    renderCircuit,
    staticMode,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || map === null || staticMode) return;
    const update = () => {
      updateMarkerDepth(map, [
        ...driverMarkersRef.current,
        ...circuitMarkersRef.current,
      ]);
    };
    map.on("moveend", update);
    update();
    return () => {
      map.off("moveend", update);
    };
  }, [circuits, drivers, locations, ready, staticMode, visibleDriverIds]);

  useEffect(() => {
    const map = mapRef.current;
    const globe = globeRef.current;
    const diagnosticsEnabled =
      new URLSearchParams(window.location.search).get("diagnostics") === "map";
    if (!ready || map === null || globe === null || !diagnosticsEnabled) return;
    const update = () => {
      const driverErrors = projectionErrors(map, driverMarkersRef.current);
      const circuitErrors = projectionErrors(map, circuitMarkersRef.current);
      globe.dataset.driverProjectionError = Math.max(
        0,
        ...driverErrors.map((entry) => entry.error),
      ).toFixed(2);
      globe.dataset.circuitProjectionError = Math.max(
        0,
        ...circuitErrors.map((entry) => entry.error),
      ).toFixed(2);
      globe.dataset.projectionDetail = JSON.stringify({
        drivers: driverErrors,
        circuits: circuitErrors,
      });
    };
    map.on("render", update);
    update();
    return () => {
      map.off("render", update);
    };
  }, [circuits, drivers, locations, ready]);

  useEffect(() => {
    if (selectedDriverId === null) return;
    const location = locations.find(
      (item) => item.driverId === selectedDriverId,
    );
    if (location?.status !== "estimated") return;
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    mapRef.current?.flyTo({
      center: location.coordinates as LngLatLike,
      zoom: 5.2,
      duration: reducedMotion ? 0 : 1_100,
      essential: false,
    });
  }, [locations, selectedDriverId]);

  const staticPresentation = useMemo(
    () => staticClusters(drivers, locations, visibleDriverIds),
    [drivers, locations, visibleDriverIds],
  );

  return (
    <div
      ref={globeRef}
      className="globe"
      data-atlas-ready={ready}
      data-circuit-loaded={loadedCircuitId ?? "none"}
      data-circuit-map-highlight={mapHighlight}
      data-circuit-renderer={circuitRenderer}
      data-cluster-count={
        staticMode
          ? Number(staticPresentation.cluster.length > 1)
          : clusterCount
      }
      data-marker-count={
        staticMode
          ? staticPresentation.cluster.length +
            staticPresentation.individuals.length
          : markerCount
      }
      data-testid="globe"
    >
      <div ref={containerRef} className="globe__map" />
      <div className="globe__tone" aria-hidden="true" />
      {failed && (
        <>
          <div className="globe__fallback" aria-hidden="true">
            <svg viewBox="0 0 640 640">
              <defs>
                <clipPath id="atlas-disc">
                  <circle cx="320" cy="320" r="274" />
                </clipPath>
                <radialGradient id="atlas-sphere" cx="35%" cy="25%" r="76%">
                  <stop offset="0" stopColor="#ffffff" />
                  <stop offset="0.72" stopColor="#fbfcff" />
                  <stop offset="1" stopColor="#dfe9fb" />
                </radialGradient>
                <pattern
                  id="atlas-dots"
                  width="16"
                  height="16"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M4 1.65 6.05 2.8v2.3L4 6.25 1.95 5.1V2.8L4 1.65Zm8 8 2.05 1.15v2.3L12 14.25 9.95 13.1v-2.3L12 9.65Z"
                    fill="#729FE8"
                    fillOpacity="0.72"
                  />
                  <path
                    d="M12 1.65 14.05 2.8v2.3L12 6.25 9.95 5.1V2.8L12 1.65ZM4 9.65 6.05 10.8v2.3L4 14.25 1.95 13.1v-2.3L4 9.65Z"
                    fill="#A9C5F3"
                    fillOpacity="0.52"
                  />
                </pattern>
              </defs>
              <circle
                className="atlas-disc"
                cx="320"
                cy="320"
                r="274"
                fill="url(#atlas-sphere)"
              />
              <g clipPath="url(#atlas-disc)">
                <path
                  className="atlas-grid"
                  d="M44 239h552M44 320h552M44 401h552M320 46c-88 74-132 165-132 274s44 200 132 274M320 46c88 74 132 165 132 274s-44 200-132 274"
                />
                <path
                  className="atlas-land"
                  d="M86 218l41-73 91-38 60 24 26 56-35 31-54-1-24 42-52 7-33-19Zm208 47 38-73 56-35 67 25 35 47-29 45-58 8-18 45-41 27-35-33Zm-8 94 56-8 46 41-13 65-40 80-32-39-19-77Zm144-21 56-41 78 9 20 58-32 40-64-10-35 30-39-32Z"
                />
                <path
                  className="atlas-road"
                  d="M112 206c59 10 107 15 150 8 37-6 78-16 121-12 72 7 116 34 173 65M151 256c42-17 79-18 112 4 48 32 83 76 153 88 47 8 87 4 125-13M314 185c-13 67-11 124 8 170 17 42 47 76 92 103"
                />
              </g>
              <circle className="atlas-rim" cx="320" cy="320" r="274" />
            </svg>
          </div>
          <div className="static-signals">
            {staticPresentation.cluster.length > 1 && (
              <button
                className="driver-cluster static-signals__cluster"
                onClick={() => {
                  onSelectGroup(
                    staticPresentation.cluster.map((driver) => driver.id),
                  );
                }}
                aria-label={`Open ${String(staticPresentation.cluster.length)} nearby drivers`}
              >
                <span className="driver-cluster__plate">
                  {staticPresentation.cluster.map((driver) => (
                    <span
                      className="driver-cluster__badge"
                      style={
                        {
                          "--driver-accent": driver.accentColor,
                        } as React.CSSProperties
                      }
                      key={driver.id}
                    >
                      <strong>{driver.acronym}</strong>
                      <small>#{driver.permanentNumber}</small>
                    </span>
                  ))}
                  <span className="driver-cluster__count">
                    +{staticPresentation.cluster.length}
                  </span>
                </span>
              </button>
            )}
            {staticPresentation.individuals.map((driver, index) => {
              const location = locations.find(
                (item) => item.driverId === driver.id,
              );
              if (location?.status !== "estimated") return null;
              return (
                <button
                  key={driver.id}
                  className={`driver-marker driver-marker--${location.confidenceBand} static-signals__driver`}
                  style={
                    {
                      left: `${String(24 + ((index * 23) % 58))}%`,
                      top: `${String(37 + ((index * 11) % 25))}%`,
                      "--driver-accent": driver.accentColor,
                    } as React.CSSProperties
                  }
                  onClick={() => {
                    onSelectDriver(driver.id);
                  }}
                  aria-label={`Open ${driver.givenName} ${driver.familyName}`}
                >
                  <span className="driver-marker__plate">
                    <span className="driver-marker__acronym">
                      {driver.acronym}
                    </span>
                    <span className="driver-marker__number">
                      #{driver.permanentNumber}
                    </span>
                  </span>
                </button>
              );
            })}
            {mapDetails.circuits &&
              circuits.map((circuit, index) => {
                return (
                  <button
                    key={circuit.id}
                    className={`circuit-marker circuit-marker--${circuit.status} circuit-marker--${circuit.assetState} static-signals__circuit`}
                    style={
                      {
                        left: `${String(15 + ((index * 29) % 70))}%`,
                        top: `${String(30 + ((index * 17) % 45))}%`,
                      } as React.CSSProperties
                    }
                    aria-label={
                      circuit.assetState === "unavailable"
                        ? `${circuit.name} circuit asset unavailable`
                        : `Open ${circuit.assetState === "3d" ? "3D " : ""}${circuit.name}`
                    }
                    onClick={() => {
                      onSelectCircuit(circuit.id);
                      void renderCircuit(circuit, true);
                    }}
                  >
                    <span className="circuit-marker__label">
                      {circuit.name}
                    </span>
                  </button>
                );
              })}
          </div>
        </>
      )}
    </div>
  );
}
